import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    addAffiliate,
    removeAffiliateCommission,
    resendAffiliateLink,
    revokeAffiliateAccess,
    sendMassEmail,
    updateAffiliateCommission,
} from '../analytics.logic';
import { stanNotify } from '@/lib';

export function useAffiliateActions(pageId: string) {
    const queryClient = useQueryClient();

    const revokeAccessMutation = useMutation({
        mutationFn: (resellerId: number) => revokeAffiliateAccess(pageId, resellerId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['affiliates', pageId] });
        },
    });

    const resendLinkMutation = useMutation({
        mutationFn: (resellerId: number) => resendAffiliateLink(pageId, resellerId),
    });

    const addAffiliateMutation = useMutation({
        mutationFn: (payload: { full_name: string; email: string }) => addAffiliate({ ...payload, page_id: pageId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['affiliates', pageId] });
        },
    });

    const updateCommissionMutation = useMutation({
        mutationFn: (payload: { affiliate_custom_percentage: number; affiliate_user_id: number }) =>
            updateAffiliateCommission({ ...payload, page_id: pageId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['affiliates', pageId] });
        },
    });

    const removeCommissionMutation = useMutation({
        mutationFn: (affiliate_user_id: number) => removeAffiliateCommission({ page_id: pageId, affiliate_user_id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['affiliates', pageId] });
        },
    });

    //TODO: Implement send mass email functionality
    const sendMassEmailMutation = useMutation({
        mutationFn: (payload: { params: { filter_array: string; timezone: string }; subject: string; body: string }) =>
            sendMassEmail(pageId, payload),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Send Email',
                text: `Email sent successfully`,
                duration: 2000,
            });
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Send Email',
                text: 'Please provide all required information',
                duration: 2000,
            });
        },
    });

    return {
        revokeAccess: revokeAccessMutation,
        resendLink: resendLinkMutation,
        addAffiliate: addAffiliateMutation,
        updateCommission: updateCommissionMutation,
        removeCommission: removeCommissionMutation,
        sendMassEmail: sendMassEmailMutation,
    };
}
